<template>
  <div
    ref="targetRef"
    class="lazy-section"
    :style="{ minHeight: sectionHeight }"
  >
    <div
      v-if="!isVisible"
      class="lazy-section__placeholder"
    />
    <slot
      v-else
      :setHeightToAuto="enableAutoHeight"
    />
  </div>
</template>

<script lang="ts" setup>
  import { computed, nextTick, watch } from 'vue';
  import { useScreenSize } from '@experience/shared';
  import { useLazySection } from '@/shared/composables/useLazySection';

  /**
   * Высота плейсхолдера для десктопной и мобильной версий
   */
  interface PlaceholderHeight {
    /** Высота для десктопа в пикселях */
    desktop?: number;
    /** Высота для мобильной версии в пикселях */
    mobile?: number;
  }

  interface Props {
    /**
     * Высота плейсхолдера до загрузки контента
     * Может быть числом (одинаковая высота для всех устройств) или объектом с отдельными значениями для desktop/mobile
     * @default 500
     */
    placeholderHeight?: PlaceholderHeight | number;
    /**
     * Отступ от области видимости для IntersectionObserver
     * Определяет, насколько раньше начнется загрузка контента до появления в viewport
     * @default '200px'
     */
    rootMargin?: string;
    /**
     * Порог видимости элемента для IntersectionObserver (от 0 до 1)
     * Значение 0.25 означает, что контент начнет загружаться, когда 25% элемента войдет в область видимости
     * @default 0.25
     */
    threshold?: number;
    /**
     * Режим управления высотой секции после загрузки контента
     * - 'auto': автоматически переключается на auto height после загрузки контента
     * - 'manual': требует ручного вызова setHeightToAuto для переключения на auto height
     * @default 'manual'
     */
    heightControl?: 'auto' | 'manual';
  }

  const props = withDefaults(defineProps<Props>(), {
    rootMargin: '200px',
    threshold: 0.25,
    heightControl: 'manual',
  });

  const DEFAULT_HEIGHT = 500;

  const isHeightFixed = defineModel<boolean>('isHeightFixed', { default: true });

  const { isMobile } = useScreenSize();
  const { targetRef, isVisible } = useLazySection({
    rootMargin: props.rootMargin,
    threshold: props.threshold,
  });

  const enableAutoHeight = () => {
    isHeightFixed.value = false;
  };

  const sectionHeight = computed(() => {
    if (!isHeightFixed.value) {
      return 'auto';
    }

    if (typeof props.placeholderHeight === 'number') {
      return `${props.placeholderHeight}px`;
    }

    if (props.placeholderHeight && typeof props.placeholderHeight === 'object') {
      const desktopHeight = props.placeholderHeight.desktop ?? DEFAULT_HEIGHT;
      const mobileHeight = props.placeholderHeight.mobile ?? DEFAULT_HEIGHT;

      return isMobile.value ? `${mobileHeight}px` : `${desktopHeight}px`;
    }

    return `${DEFAULT_HEIGHT}px`;
  });

  watch(isVisible, async (value) => {
    if (value && props.heightControl === 'auto') {
      await nextTick();
      enableAutoHeight();
    }
  });

  defineExpose({ setHeightToAuto: enableAutoHeight });
</script>

<style lang="scss" scoped>
  .lazy-section {
    width: 100%;
  }
</style>
